import React, { useState, useRef, useEffect } from 'react';
import { Heart, X, ShoppingBag, Users, Zap, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatCurrency } from '@/utils/formatting';

export interface Product {
  id: string;
  title: string;
  description: string;
  price: number;
  currency: string;
  images: string[];
  vendorUrl: string;
  tags: string[];
  rating?: number;
  reviewCount?: number;
  isInStock: boolean;
}

export interface SwipeAction {
  productId: string;
  action: 'love' | 'pass' | 'maybe' | 'add_to_cart';
  userId: string;
  timestamp: Date;
  velocity?: number;
}

interface SwipeableProductCardProps {
  product: Product;
  onSwipe: (action: SwipeAction['action']) => void;
  onAddToCart: () => void;
  participantActions: Map<string, SwipeAction['action']>;
  isCurrentCard: boolean;
  className?: string;
}

export const SwipeableProductCard: React.FC<SwipeableProductCardProps> = ({
  product,
  onSwipe,
  onAddToCart,
  participantActions,
  isCurrentCard,
  className = '',
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const dragThreshold = 100;
  const swipeThreshold = 150;

  // Touch and mouse event handlers
  const handleStart = (clientX: number) => {
    setIsDragging(true);
    setDragStartX(clientX);
    setDragOffset(0);
  };

  const handleMove = (clientX: number) => {
    if (!isDragging) return;
    
    const offset = clientX - dragStartX;
    setDragOffset(offset);
    
    // Determine swipe direction and intensity
    if (Math.abs(offset) > dragThreshold) {
      setSwipeDirection(offset > 0 ? 'right' : 'left');
    } else {
      setSwipeDirection(null);
    }
  };

  const handleEnd = () => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    // Determine if swipe was strong enough
    const absOffset = Math.abs(dragOffset);
    if (absOffset > swipeThreshold) {
      if (dragOffset > 0) {
        // Swipe right - Love it!
        onSwipe('love');
      } else {
        // Swipe left - Pass
        onSwipe('pass');
      }
    } else {
      // Reset position
      setDragOffset(0);
    }
    
    setSwipeDirection(null);
  };

  // Mouse events
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    handleStart(e.clientX);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    e.preventDefault();
    handleMove(e.clientX);
  };

  const handleMouseUp = () => {
    handleEnd();
  };

  // Touch events
  const handleTouchStart = (e: React.TouchEvent) => {
    handleStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    handleMove(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    handleEnd();
  };

  // Image navigation
  const nextImage = () => {
    setCurrentImageIndex((prev) => 
      prev < product.images.length - 1 ? prev + 1 : 0
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => 
      prev > 0 ? prev - 1 : product.images.length - 1
    );
  };

  // Button handlers
  const handlePass = () => onSwipe('pass');
  const handleMaybe = () => onSwipe('maybe');
  const handleLove = () => onSwipe('love');

  // Calculate card transform
  const getTransform = () => {
    const rotation = dragOffset * 0.1; // Subtle rotation
    return `translateX(${dragOffset}px) rotate(${rotation}deg)`;
  };

  // Get swipe indicator color and opacity
  const getSwipeIndicator = () => {
    const absOffset = Math.abs(dragOffset);
    const opacity = Math.min(absOffset / swipeThreshold, 1);
    
    if (dragOffset > dragThreshold) {
      return { color: 'text-green-500', bg: 'bg-green-100', opacity, text: 'LOVE IT!' };
    } else if (dragOffset < -dragThreshold) {
      return { color: 'text-red-500', bg: 'bg-red-100', opacity, text: 'PASS' };
    }
    return null;
  };

  const swipeIndicator = getSwipeIndicator();

  // Count participant reactions
  const reactionCounts = {
    love: Array.from(participantActions.values()).filter(action => action === 'love').length,
    pass: Array.from(participantActions.values()).filter(action => action === 'pass').length,
    maybe: Array.from(participantActions.values()).filter(action => action === 'maybe').length,
  };

  if (!isCurrentCard) {
    return null;
  }

  return (
    <div className={`swipeable-product-card relative ${className}`}>
      {/* Card */}
      <div
        ref={cardRef}
        className={`relative bg-white rounded-2xl shadow-2xl overflow-hidden transition-all duration-200 select-none ${
          isDragging ? 'cursor-grabbing scale-105' : 'cursor-grab'
        }`}
        style={{
          transform: getTransform(),
          zIndex: isDragging ? 10 : 1,
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={isDragging ? handleMouseMove : undefined}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleEnd}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Swipe Indicator */}
        {swipeIndicator && (
          <div 
            className={`absolute inset-0 ${swipeIndicator.bg} flex items-center justify-center z-10`}
            style={{ opacity: swipeIndicator.opacity * 0.9 }}
          >
            <div className={`text-4xl font-bold ${swipeIndicator.color} transform rotate-12`}>
              {swipeIndicator.text}
            </div>
          </div>
        )}

        {/* Product Images */}
        <div className="relative h-96 bg-gray-200">
          {product.images.length > 0 ? (
            <>
              <img
                src={product.images[currentImageIndex]}
                alt={product.title}
                className="w-full h-full object-cover"
                draggable={false}
              />
              
              {/* Image Navigation */}
              {product.images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  
                  {/* Image Dots */}
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                    {product.images.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          index === currentImageIndex ? 'bg-white' : 'bg-white bg-opacity-50'
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              <ShoppingBag className="w-16 h-16" />
            </div>
          )}

          {/* Stock Status */}
          {!product.isInStock && (
            <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
              Out of Stock
            </div>
          )}

          {/* Participant Reactions Overlay */}
          {reactionCounts.love + reactionCounts.pass + reactionCounts.maybe > 0 && (
            <div className="absolute top-4 left-4 flex gap-2">
              {reactionCounts.love > 0 && (
                <div className="bg-green-500 text-white px-2 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                  <Heart className="w-3 h-3 fill-current" />
                  {reactionCounts.love}
                </div>
              )}
              {reactionCounts.pass > 0 && (
                <div className="bg-red-500 text-white px-2 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                  <X className="w-3 h-3" />
                  {reactionCounts.pass}
                </div>
              )}
              {reactionCounts.maybe > 0 && (
                <div className="bg-yellow-500 text-white px-2 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                  <Zap className="w-3 h-3" />
                  {reactionCounts.maybe}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{product.title}</h2>
              <p className="text-gray-600 line-clamp-3">{product.description}</p>
            </div>
            <div className="text-right ml-4">
              <div className="text-3xl font-bold text-green-600">
                {formatCurrency(product.price, product.currency)}
              </div>
              {product.rating && (
                <div className="flex items-center justify-end gap-1 mt-1">
                  <span className="text-yellow-400">★</span>
                  <span className="text-sm text-gray-600">
                    {product.rating} ({product.reviewCount})
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Tags */}
          {product.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {product.tags.slice(0, 4).map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                >
                  {tag}
                </span>
              ))}
              {product.tags.length > 4 && (
                <span className="px-3 py-1 bg-gray-100 text-gray-500 rounded-full text-sm">
                  +{product.tags.length - 4} more
                </span>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              onClick={handlePass}
              className="flex-1 bg-red-50 hover:bg-red-100 text-red-600 font-semibold py-3 px-6 rounded-xl transition-all flex items-center justify-center gap-2"
            >
              <X className="w-5 h-5" />
              Pass
            </button>
            
            <button
              onClick={handleMaybe}
              className="flex-1 bg-yellow-50 hover:bg-yellow-100 text-yellow-600 font-semibold py-3 px-6 rounded-xl transition-all flex items-center justify-center gap-2"
            >
              <Zap className="w-5 h-5" />
              Maybe
            </button>
            
            <button
              onClick={handleLove}
              className="flex-1 bg-green-50 hover:bg-green-100 text-green-600 font-semibold py-3 px-6 rounded-xl transition-all flex items-center justify-center gap-2"
            >
              <Heart className="w-5 h-5" />
              Love It!
            </button>
          </div>

          {/* Add to Cart */}
          {product.isInStock && (
            <button
              onClick={onAddToCart}
              className="w-full mt-4 bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-6 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg"
            >
              <ShoppingBag className="w-5 h-5" />
              Add to Shared Cart
            </button>
          )}
        </div>
      </div>

      {/* Swipe Instructions */}
      <div className="absolute -bottom-16 left-1/2 transform -translate-x-1/2 text-center">
        <div className="text-gray-500 text-sm">
          Swipe left to pass • Swipe right to love • Tap buttons to choose
        </div>
      </div>
    </div>
  );
};